import { useState, useEffect, useCallback } from 'react';
import { alertService, Alert, AlertStats } from '@/services/alertService';
import { useAuth } from './useAuth';

export interface UseAlertsReturn {
  alerts: Alert[];
  stats: AlertStats;
  isLoading: boolean;
  refresh: () => Promise<void>;
  markAsRead: (alertId: string) => void;
  markAllAsRead: () => void;
  dismissAlert: (alertId: string) => void;
  clearAlerts: () => void;
}

export function useAlerts(): UseAlertsReturn {
  const { currentRestaurantId } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // S'abonner aux changements d'alertes
  useEffect(() => {
    const unsubscribe = alertService.subscribe((newAlerts) => {
      setAlerts(newAlerts);
    });

    return unsubscribe;
  }, []);

  // Rafraîchir les alertes
  const refresh = useCallback(async () => {
    if (!currentRestaurantId) return;

    setIsLoading(true);
    try {
      await alertService.forceCheck(currentRestaurantId);
    } finally {
      setIsLoading(false);
    }
  }, [currentRestaurantId]);

  // Vérifier les alertes au montage et quand le restaurant change
  useEffect(() => {
    if (currentRestaurantId) {
      refresh();
    }
  }, [currentRestaurantId, refresh]);

  // Calculer les statistiques
  const stats = alertService.getAlertStats();

  return {
    alerts,
    stats,
    isLoading,
    refresh,
    markAsRead: alertService.markAsRead.bind(alertService),
    markAllAsRead: alertService.markAllAsRead.bind(alertService),
    dismissAlert: alertService.dismissAlert.bind(alertService),
    clearAlerts: alertService.clearAlerts.bind(alertService),
  };
}
